import * as React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { withRouter, RouteComponentProps, Redirect } from "react-router-dom";
import { Helmet } from "react-helmet";

import { HAS_USER_LOGGED_OUT } from "@config/consts";

import { AuthGuardState } from "@typings/locations";
import {
  AUTH_REDIRECTS,
  AVAILABLE_LANGS,
  SimpleObject,
} from "@typings/globals";
import { USER_LOGIN_STATE } from "@typings/user";
import { MODALS } from "@typings/modals";

import { Container } from "@ui/Atoms/Grid";
import { SmallHeading } from "@ui/Atoms/Headings";
import { Section, Paragraph, OptionPanel } from "@ui/Atoms";

import { sessionStorageFacade } from "@services/SessionStorageFacade";
import { stringToBool } from "@services/StringToBool";

import { useTranslate } from "@hooks/useTranslate";

import { IStore } from "@soft/redux/reducers";
import { getUserState, isUserLoggedIn } from "@soft/redux/user/user.selectors";
import { showModal } from "@soft/redux/modals/modals.actions";
import { getLanguage } from "@soft/redux/localisation/localisation.selectors";

interface Props extends RouteComponentProps<null, any, AuthGuardState> {
  children: React.ReactNode;
  getUserState: USER_LOGIN_STATE;
  isUserLoggedIn: boolean;
  language: AVAILABLE_LANGS;
  showModal: (id: string, options?: SimpleObject) => void;
}

export const RedirectionComponent = (
  props: Props,
): React.ReactElement | null => {
  const translate = useTranslate("batsoft_pwa_generic");
  const sessionStorage = sessionStorageFacade();
  const state = props.history.location.state;
  const locationState = props.location.state;

  const hasUserLoggedOut = stringToBool(
    sessionStorage.getItem(HAS_USER_LOGGED_OUT) || "",
  );

  React.useEffect(() => {
    // when someone tries to enter a user page without being logged in
    if (locationState && locationState.originalRoute && !hasUserLoggedOut) {
      props.showModal(MODALS.NAVBAR_LOGIN, {
        onOAuthLoginSuccess: () => {
          props.history.push(locationState.originalRoute);
        },
        onLoginSuccess: () => {
          props.history.push(locationState.originalRoute);
        },
        onClose: () => {
          window.location.href = `/${props.language}/`;
        },
      });
    }

    // redirect user to the homepage when he just logged out intentionally (in PWA)
    if (hasUserLoggedOut) {
      sessionStorage.setItem(HAS_USER_LOGGED_OUT, "false");
      window.location.href = "/";
    }
  }, []);

  if (props.getUserState === USER_LOGIN_STATE.UNKNOWN) {
    return null;
  }

  if (
    state &&
    state.referer &&
    state.reason === AUTH_REDIRECTS.UNAUTHENTICATED &&
    props.isUserLoggedIn
  ) {
    return (
      <Redirect
        to={{
          pathname: state.referer,
          search: props.location ? props.location.search : "",
        }}
        push={false}
      />
    );
  }

  return (
    <Container>
      <Helmet>
        <title>Redirecting...</title>
      </Helmet>
      <Section small>
        <OptionPanel>
          <SmallHeading>
            {translate("batsoft_pwa_generic.redirection_title")}
          </SmallHeading>
          <Paragraph>
            {translate("batsoft_pwa_generic.redirection_body")}
          </Paragraph>
        </OptionPanel>
      </Section>
    </Container>
  );
};

const mapState = (store: IStore) => ({
  getUserState: getUserState(store),
  isUserLoggedIn: isUserLoggedIn(store),
  language: getLanguage(store),
});

const mapDispatch = {
  showModal,
};

export const Redirection = compose(
  withRouter,
  connect(mapState, mapDispatch),
)(RedirectionComponent);
